import { useEffect, useRef } from 'react';

/**
 * APPLYING A PREFILL TO A DIALOG THAT IS ALREADY MOUNTED.
 *
 * <h2>⚠ THE OBVIOUS IMPLEMENTATION WIPES WHAT THE USER IS TYPING</h2>
 *
 * The brief asks for "a `useEffect` watching `initialConfig` so that if new configuration props
 * are passed while mounted, the state updates accordingly". Written as
 * {@code useEffect(apply, [initialConfig])} that is a trap: an object literal passed inline from
 * a parent is a NEW object on every render of that parent, so the effect re-runs on every
 * unrelated re-render and resets every field — mid-keystroke, with no error and no way for the
 * user to tell what happened.
 *
 * <p>These four dialogs sit under `App`, which re-renders on a chart zoom, a symbol load, an
 * overlay change and every tick of a replay. The naive version would be unusable.
 *
 * <h2>So: applied ONCE PER DISTINCT CONFIG OBJECT</h2>
 *
 * The DEPENDENCY ARRAY is what does that: React compares it with `Object.is`, so a parent
 * re-render passing the same object does not re-run the effect at all. Identity rather than a
 * deep compare because the producer controls it — `App` holds the prefill in state, so a new
 * object means a new command, which is exactly the signal wanted. A deep compare would also
 * silently re-apply a prefill that happened to equal what the user had just typed over it.
 *
 * <h2>⚠ AND THE REF IS FOR STRICTMODE ALONE, which is the only case the deps do not cover</h2>
 *
 * `index.js` wraps the app in `<React.StrictMode>`, so in development React runs every effect
 * TWICE on mount — same component instance, same deps, two invocations, with the ref surviving
 * in between. Without it the prefill applies twice: harmless for a plain assignment, not
 * harmless for an applier that appends or counts, and it makes "how many times did this run"
 * untrustworthy in exactly the environment it is being debugged in.
 *
 * <p>FOUND BY SABOTAGE: removing the ref broke no test, because every other case is already
 * handled by the dependency array. The test that distinguishes them renders inside StrictMode.
 *
 * <h2>Null is "no prefill", not "reset to defaults"</h2>
 *
 * Clearing the prop must not wipe the form. A dialog opened by hand always has
 * `initialConfig == null`, so treating that as an instruction would make every manual open fight
 * whatever the user last typed — and it would run on close, when the parent clears the prefill.
 */
export function useInitialConfig(initialConfig, apply) {
  const appliedRef = useRef(null);
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useEffect(() => {
    if (!initialConfig) return;
    if (appliedRef.current === initialConfig) return;
    appliedRef.current = initialConfig;
    applyRef.current(initialConfig);
    // `apply` is read through a ref, so a caller may pass an inline arrow without this effect
    // re-running -- which is the same trap one level down, and every call site would hit it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig]);
}

/**
 * `prefill[key]` when it is present, otherwise the current value.
 *
 * <p>⚠ `undefined` ONLY, never falsy. A prefill of `0`, `''` or `false` is a real value somebody
 * asked for — `?? ` rather than `||` is the whole point, and `||` here would make "set the
 * threshold to 0" silently keep the default of 2.
 */
export function pick(prefill, key, current) {
  const v = prefill ? prefill[key] : undefined;
  return v === undefined ? current : v;
}

/**
 * A setter-shaped applier: merges the named keys of a prefill into an object state.
 *
 * <p>MERGES rather than replaces, so a prefill naming one field leaves the rest of the form
 * alone. Replacing would mean a Copilot that set one condition's planet had silently cleared
 * every other field in that form.
 *
 * @param setState the `useState` setter for an object-shaped form
 * @param prefill  the incoming config
 * @param keys     which keys of `prefill` belong to this form
 */
export function mergeInto(setState, prefill, keys) {
  if (!prefill) return;
  const patch = {};
  keys.forEach((k) => { if (prefill[k] !== undefined) patch[k] = prefill[k]; });
  if (Object.keys(patch).length === 0) return;
  setState(prev => ({ ...prev, ...patch }));
}

export default useInitialConfig;
